import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ExtractionFileRepository } from '../../../../domain/service/associate-extraction/file/extraction-file.repository';
import {
  ExtractionFileUploadResponse,
  SpecimenForExtraction,
  ExtractionAssociation,
  AssociateExtractionsResponse
} from '../../../../domain/model/associate-extraction/file/extraction-file-upload.model';
import { HttpErrorHandlerService } from '../../../../core/error-handling/http-error-handler.service';
import { environment } from 'src/environments/environment';

/**
 * Backend response DTO for extraction file upload.
 */
interface ExtractionFileBackendResponse {
  fileName: string;
  success: boolean;
  processedCount: number;
  skippedCount: number;
  message: string;
  createdExtractionIds: string[];
  validationErrors: {
    rowNumber: number;
    fieldName: string;
    invalidValue: string;
    errorDescription: string;
    errorCode: string;
  }[];
  extractionRecords: {
    rowIndex: number;
    wellLocation: string;
    concentration: string;
    ratio260_230: string;
    ratio260_280: string;
    extractionId: string;
  }[];
}

/**
 * Extraction File Repository Adapter
 *
 * Implements the ExtractionFileRepository interface defined in the domain layer.
 * Handles HTTP communication for extraction file uploads using multipart/form-data.
 *
 * Architecture Layer: Infrastructure
 * - Implements domain interface (Dependency Inversion)
 * - Uses HttpClient for HTTP operations
 * - Transforms backend DTOs to domain models
 *
 * HU24 - Cargar Datos de Extracción mediante archivo
 */
@Injectable({
  providedIn: 'root'
})
export class ExtractionFileRepositoryAdapter extends ExtractionFileRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}`;
  private readonly errorHandler = inject(HttpErrorHandlerService);

  /**
   * Upload an extraction file to the backend for processing.
   *
   * @param file - The Excel file to upload (.xlsx or .xls)
   * @returns Observable with the upload response including validation results
   */
  uploadExtractionFile(file: File): Observable<ExtractionFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ExtractionFileBackendResponse>(
        `${this.BASE_URL}/extractions/upload-file`,
        formData
      )
      .pipe(
        map((response) => this.mapToModel(response)),
        catchError((error: unknown) =>
          this.errorHandler.handleError(
            'uploadExtractionFile',
            error as HttpErrorResponse
          )
        )
      );
  }

  /**
   * Gets specimens with PENDING_EXTRACTION status for extraction association.
   *
   * @returns Observable with the list of specimens available for extraction
   */
  getSpecimensPendingExtraction(): Observable<SpecimenForExtraction[]> {
    return this.http
      .get<
        SpecimenForExtraction[]
      >(`${this.BASE_URL}/specimens/extraction/pending`)
      .pipe(
        catchError((error: unknown) =>
          this.errorHandler.handleError(
            'getSpecimensPendingExtraction',
            error as HttpErrorResponse
          )
        )
      );
  }

  /**
   * Associates LG codes with extractions.
   * Updates extraction status to IN_PROGRESS.
   *
   * @param associations - Array of extraction-LG pairs
   * @returns Observable with the association result
   */
  associateExtractions(
    associations: ExtractionAssociation[]
  ): Observable<AssociateExtractionsResponse> {
    return this.http
      .post<AssociateExtractionsResponse>(
        `${this.BASE_URL}/extractions/associate`,
        associations
      )
      .pipe(
        catchError((error: unknown) =>
          this.errorHandler.handleError(
            'associateExtractions',
            error as HttpErrorResponse
          )
        )
      );
  }

  /**
   * Map backend DTO to domain model.
   */
  private mapToModel(
    dto: ExtractionFileBackendResponse
  ): ExtractionFileUploadResponse {
    return {
      fileName: dto.fileName,
      success: dto.success,
      processedCount: dto.processedCount,
      skippedCount: dto.skippedCount,
      message: dto.message,
      validationErrors: (dto.validationErrors || []).map((error) => ({
        rowNumber: error.rowNumber,
        fieldName: error.fieldName,
        invalidValue: error.invalidValue,
        errorDescription: error.errorDescription,
        errorCode: error.errorCode
      })),
      extractionRecords: (dto.extractionRecords || []).map((record) => ({
        rowIndex: record.rowIndex,
        wellLocation: record.wellLocation,
        concentration: record.concentration,
        ratio260_230: record.ratio260_230,
        ratio260_280: record.ratio260_280
      }))
    };
  }
}
